import { useState } from "react";
import { toast } from "sonner";
import { Check, Mail } from "lucide-react";
import { api } from "@/lib/api";

export default function QuizEmailCapture({ quizId, element }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !quizId) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/quiz/${quizId}/email`, { email, name: name || null });
      setSaved(true);
      toast.success("Result saved. We'll be in touch with seasonal notes for your element.");
    } catch {
      toast.error("Couldn't save right now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (saved) {
    return (
      <div
        className="mt-16 p-10 lg:p-12 border flex items-start gap-5"
        style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
        data-testid="quiz-email-saved"
      >
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "var(--sano-olive)", color: "var(--sano-bg)" }}
        >
          <Check size={18} strokeWidth={1.5} />
        </div>
        <div>
          <div className="eyebrow mb-2">Saved</div>
          <div className="font-serif-display text-2xl leading-snug mb-1">
            Your {element} profile is on its way.
          </div>
          <p className="text-sm" style={{ color: "var(--sano-ink-2)" }}>
            We&apos;ll keep this gentle — a seasonal letter, a new workshop, the occasional
            recipe shaped to your element.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-16 p-10 lg:p-12 border"
      style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
      data-testid="quiz-email-capture"
    >
      <div className="flex items-center gap-2 eyebrow mb-3">
        <Mail size={14} strokeWidth={1.5} /> Save my result
      </div>
      <h3 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-3">
        Want your {element} profile saved?
      </h3>
      <p className="text-sm leading-relaxed mb-6 max-w-xl" style={{ color: "var(--sano-ink-2)" }}>
        Leave your email and we&apos;ll keep your result on file — plus send you the
        occasional seasonal note about food, herbs and rituals for {element} types.
        No noise. Unsubscribe any time.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <input
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
          style={{ borderColor: "var(--sano-border)" }}
          data-testid="quiz-email-name"
        />
        <input
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
          style={{ borderColor: "var(--sano-border)" }}
          data-testid="quiz-email-input"
        />
      </div>

      <button
        type="submit"
        disabled={loading || !quizId}
        className="px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
        style={{ background: "var(--sano-olive)" }}
        data-testid="quiz-email-submit"
      >
        {loading ? "Saving…" : "Save my result"}
      </button>
      {!quizId && (
        <div className="mt-3 text-xs" style={{ color: "var(--sano-ink-2)" }}>
          Finishing up — one moment…
        </div>
      )}
    </form>
  );
}
